// Tenant-Admin/Subscription/InvoiceList.jsx
import React, { useState } from 'react';
import { 
  FiFileText, 
  FiDownload, 
  FiEye, 
  FiCheckCircle, 
  FiClock,
  FiAlertCircle,
  FiLoader
} from 'react-icons/fi';
import api from '../../api';
import { useToast } from '../../super-admin/components/ToastContainer';

const InvoiceList = ({ invoices, summary, onViewInvoice }) => {
  const { success, error: showError } = useToast();
  const [downloading, setDownloading] = useState(null);

  if (!invoices || invoices.length === 0) {
    return (
      <div className="bg-white rounded-2xl border border-slate-200 p-8 text-center flex flex-col items-center justify-center">
        <div className="p-3 bg-slate-50 text-slate-400 rounded-2xl mb-3">
          <FiFileText className="w-6 h-6" />
        </div>
        <h3 className="text-sm font-bold text-slate-800">No Invoices Found</h3>
        <p className="text-xs text-slate-400 mt-1 max-w-xs leading-normal">
          Your official payment receipts will show up here as soon as a successful transaction is processed.
        </p>
      </div>
    );
  }

  const getStatusStyle = (status) => {
    switch(status) {
      case 'paid': return 'bg-emerald-50 text-emerald-700 border-emerald-100';
      case 'pending': return 'bg-amber-50 text-amber-700 border-amber-100';
      case 'overdue': return 'bg-rose-50 text-rose-700 border-rose-100';
      case 'refunded': return 'bg-purple-50 text-purple-700 border-purple-100';
      default: return 'bg-slate-50 text-slate-700 border-slate-100';
    }
  };

  const getStatusIcon = (status) => {
    switch(status) {
      case 'paid': return <FiCheckCircle className="w-3.5 h-3.5" />;
      case 'pending': return <FiClock className="w-3.5 h-3.5" />;
      case 'overdue': return <FiAlertCircle className="w-3.5 h-3.5" />;
      default: return <FiFileText className="w-3.5 h-3.5" />;
    }
  };

  // ✅ Download Invoice PDF
  const handleDownload = async (invoice) => {
    setDownloading(invoice.id);
    try {
      const token = localStorage.getItem('token');
      const response = await api.get(
        `/api/tenant-admin/invoices/${invoice.id}/download`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${invoice.invoiceNumber}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      success('Invoice downloaded successfully!');
    } catch (err) {
      console.error('❌ Error downloading invoice:', err);
      showError(err.response?.data?.message || 'Could not download your invoice file.');
    } finally {
      setDownloading(null);
    }
  };

  return (
    <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-sm">
      
      {/* Header Summary Dashboard Block */}
      <div className="px-5 py-4 border-b border-slate-100 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between bg-white">
        <div>
          <h2 className="text-sm font-black text-slate-900 uppercase tracking-wide flex items-center gap-2">
            <FiFileText className="w-4 h-4 text-indigo-500" />
            <span>Invoice History</span>
          </h2>
          <p className="text-[11px] text-slate-400 mt-0.5">
            Total of {invoices.length} billing statements logged.
          </p>
        </div>
        
        {summary && (
          <div className="flex items-center gap-3 text-xs bg-slate-50 border border-slate-100 rounded-xl p-2.5 self-start sm:self-auto">
            <div className="flex items-center gap-1 text-emerald-700 font-bold">
              <span>Total Paid:</span>
              <span>₹{summary.totalAmount?.toFixed(2) || 0}</span>
            </div>
            <span className="text-slate-200">|</span>
            <div className="flex items-center gap-1 text-amber-700 font-bold">
              <span>Pending:</span>
              <span>{summary.pending || 0}</span>
            </div>
          </div>
        )}
      </div>

      {/* Main Container Layout */}
      <div className="w-full">
        {/* Responsive Desktop Only Table Header Row */}
        <div className="hidden md:grid grid-cols-12 bg-slate-50 border-b border-slate-200 px-5 py-2.5 text-[10px] font-bold text-slate-400 uppercase tracking-wider">
          <div className="col-span-3">Invoice Identification</div>
          <div className="col-span-3">Date Issued</div>
          <div className="col-span-3">Total Charged</div>
          <div className="col-span-2">Payment Status</div>
          <div className="col-span-1 text-right">Actions</div>
        </div>

        {/* Dynamic Mobile-First Card Row Iteration mapping loop */}
        <div className="divide-y divide-slate-100">
          {invoices.map((invoice) => (
            <div 
              key={invoice.id} 
              className="grid grid-cols-1 md:grid-cols-12 items-start md:items-center p-5 md:px-5 md:py-3.5 gap-3 md:gap-0 hover:bg-slate-50/40 transition"
            >
              
              {/* Box Block 1: Invoice Identifier Reference metadata */}
              <div className="col-span-3 text-xs">
                <span className="text-[10px] md:hidden font-bold text-slate-400 uppercase tracking-wider block mb-0.5">Invoice Reference</span>
                <p className="font-bold text-slate-900 text-sm md:text-xs">{invoice.invoiceNumber}</p>
                <p className="text-[11px] text-slate-400 mt-0.5">{invoice.displayName || invoice.subscriptionPlanName}</p>
              </div>

              {/* Box Block 2: Date Issued metrics info */}
              <div className="col-span-3 text-xs">
                <span className="text-[10px] md:hidden font-bold text-slate-400 uppercase tracking-wider block mb-0.5">Date Issued</span>
                <span className="font-medium text-slate-600">
                  {new Date(invoice.invoiceDate).toLocaleDateString('en-US', {
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric'
                  })}
                </span>
              </div>

              {/* Box Block 3: Cost and Taxes accounting summary metrics */}
              <div className="col-span-3 text-xs">
                <span className="text-[10px] md:hidden font-bold text-slate-400 uppercase tracking-wider block mb-0.5">Amount Charged</span>
                <div className="flex items-baseline gap-1">
                  <span className="font-bold text-slate-900">₹{invoice.amount?.toFixed(2) || 0}</span>
                  {invoice.taxPercentage > 0 && (
                    <span className="text-[10px] text-slate-400 font-normal">
                      (incl. {invoice.taxPercentage}% tax)
                    </span>
                  )}
                </div>
              </div>

              {/* Box Block 4: Processing Verification Pill Tag label */}
              <div className="col-span-2 flex items-center">
                <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 text-[10px] font-bold rounded-md border uppercase tracking-wide ${getStatusStyle(invoice.status)}`}>
                  {getStatusIcon(invoice.status)}
                  <span>{invoice.status}</span>
                </span>
              </div>

              {/* Box Block 5: Trigger Actions Column Row elements panel */}
              <div className="col-span-1 flex items-center justify-end gap-2 pt-2 md:pt-0 border-t border-slate-100 md:border-none w-full md:w-auto">
                <button
                  type="button"
                  onClick={() => onViewInvoice(invoice)}
                  className="p-2 border border-slate-200 hover:border-indigo-200 hover:bg-indigo-50/50 text-slate-600 hover:text-indigo-600 rounded-xl transition flex-1 md:flex-initial flex items-center justify-center gap-1 text-xs font-bold md:font-normal md:p-1.5"
                  title="Preview Statement Details"
                >
                  <FiEye className="w-4 h-4 md:w-3.5 md:h-3.5" />
                  <span className="md:hidden">View Details</span>
                </button>

                <button
                  type="button"
                  onClick={() => handleDownload(invoice)}
                  disabled={downloading === invoice.id}
                  className="p-2 border border-slate-200 hover:border-emerald-200 hover:bg-emerald-50/50 text-slate-600 hover:text-emerald-600 rounded-xl transition flex-1 md:flex-initial flex items-center justify-center gap-1 text-xs font-bold md:font-normal md:p-1.5 disabled:opacity-40"
                  title="Download Raw Invoice File"
                >
                  {downloading === invoice.id ? (
                    <FiLoader className="w-4 h-4 md:w-3.5 md:h-3.5 text-emerald-600 animate-spin" />
                  ) : (
                    <>
                      <FiDownload className="w-4 h-4 md:w-3.5 md:h-3.5" />
                      <span className="md:hidden">Download File</span>
                    </>
                  )}
                </button>
              </div>

            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default InvoiceList;